import Badge, { BadgeVariant } from "@/components/custom/badge";
import Card from "@/components/custom/card";
import FilePreview from "@/components/custom/file-preview";
import { currencyFormatter, formatDate } from "@/lib/utils";
import { CaretLeft } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loading from "@/components/custom/loading";
import { toast } from "@/hooks/use-toast";
import { useGetCurrencies } from "@/services/queries/currency";
import {
  ApiResponse,
  Attachment,
  Budget,
  Invoice,
  InvoiceLineItem,
  Vendor,
  Vessel,
} from "@/lib/types";
import { Currency } from "@/lib/types";
import { TFunction } from "i18next";
import { INVOICE_STATUS } from "@/lib/constants";
import { useGetVendorById } from "@/services/queries/vendor";
import { useGetBudgetById } from "@/services/queries/budgets";
import { useGetVesselById } from "@/services/queries/vessel";
import { api, BASE_URL } from "@/lib/api";
import { Mixpanel } from "@/lib/mixpanel";
import { useGetInvoiceById } from "@/services/queries/invoices";
import EditInvoiceForm from "../invoices/components/edit-invoice-form";
import { useHasPermission } from "@/hooks/use-permission";
import { PERMISSIONS } from "@/lib/constants/permissions";

const InvoiceDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const {
    data: invoiceDetailData,
    isPending,
    isError,
    refetch,
  } = useGetInvoiceById({ id: id ?? "" });
  const [openEditInvoiceForm, setOpenEditInvoiceForm] = useState(false);
  const canWriteExpenses = useHasPermission(PERMISSIONS.Expenses.Write);

  const invoice = useMemo(
    () => invoiceDetailData?.data.value,
    [invoiceDetailData]
  );

  const { data: currenciesData } = useGetCurrencies();
  const { data: vendorData } = useGetVendorById(invoice?.vendorId);
  const { data: budgetData } = useGetBudgetById(invoice?.budgetId);
  const { data: vesselData } = useGetVesselById(invoice?.vesselId);

  const currencies = useMemo(() => currenciesData?.value, [currenciesData]);
  const currency = useMemo(() => {
    return currencies?.find((currency) => currency.id === invoice?.currencyId);
  }, [currencies, invoice?.currencyId]);

  const vendor = useMemo(() => vendorData?.value, [vendorData]);
  const budget = useMemo(() => budgetData?.value, [budgetData]);
  const vessel = useMemo(() => vesselData?.value, [vesselData]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (isError) {
      navigate(
        location.state?.redirectPath ? "/" + location.state?.redirectPath : "/"
      );
      toast({
        title: t("error_message.invoice_not_found"),
        description: t("error_message.invoice_not_found_description"),
        variant: "destructive",
      });
    }
  }, [isError, location.state?.redirectPath, navigate, t]);

  return (
    <>
      <title>{t("title.invoice_details")}</title>

      <div data-testid="invoice-detail-page" className="px-5">
        <div className="flex items-center justify-between">
          <button
            aria-label="Back button"
            onClick={() => {
              navigate(
                location.state?.redirectPath
                  ? "/" + location.state?.redirectPath
                  : "/invoices"
              );

              Mixpanel.track("Back Button Clicked");
            }}
          >
            <CaretLeft size={20} color="#FFFFFF" />
          </button>
          {canWriteExpenses && (
            <button
              className="text-[15px] font-medium text-[#45D3EC]"
              onClick={() => setOpenEditInvoiceForm(true)}
            >
              {t("button.edit")}
            </button>
          )}
        </div>

        <EditInvoiceForm
          open={openEditInvoiceForm}
          setOpen={setOpenEditInvoiceForm}
          invoice={invoice ?? ({} as Invoice)}
          refetch={refetch}
        />

        {isPending || !invoice ? (
          <Loading className="mt-10" />
        ) : isError ? (
          <div className="mt-10">
            <h1 className="text-center text-2xl font-medium text-white">
              {t("error_message.invoice_not_found")}
            </h1>
          </div>
        ) : (
          <>
            {renderHeader(currency, invoice, t)}

            <div className="mt-10 w-full space-y-[15px]">
              {renderCostCenter(invoice.vesselName, t)}
              {renderStatus(invoice.status, t)}
              {renderVessel(vessel, t)}
              {renderBudget(budget, t)}
              {renderInvoiceNumber(invoice.invoiceNumber, t)}
              {renderIssueDate(invoice.issueDate, t)}
              {renderDueDate(invoice.dueDate, t)}
              {renderVendor(vendor, t)}
              <FilesPreview files={invoice.attachments} />
              {renderNote(invoice.description, t)}
              {renderLineItems(invoice.lineItems, t, currency)}
            </div>
          </>
        )}
      </div>
    </>
  );
};

const renderHeader = (
  currency: Currency | undefined,
  invoice: Invoice,
  t: TFunction
) => {
  const invoiceStatus = INVOICE_STATUS.find(
    (status) => status.value === invoice.status
  );

  return (
    <div className="mt-10 space-y-5 px-3">
      <div className="flex flex-col gap-1">
        <span className="text-xl text-white/80">{t("label.invoice")}</span>
        <span className="text-[40px] font-semibold leading-[44px] text-white">
          {currencyFormatter(invoice.totalAmount, currency?.code)}
        </span>
      </div>

      <Badge
        label={t(`invoice_status.${invoiceStatus?.translationKey}`)}
        variant={invoiceStatus?.variant as BadgeVariant}
        icon={invoiceStatus?.icon}
      />
    </div>
  );
};

const renderCostCenter = (name: string | undefined, t: TFunction) => {
  if (!name) return null;

  return (
    <Card className="h-[66px] py-[14px]">
      <div className="flex flex-col items-start justify-between">
        <span className="text-xs leading-4 text-white/50">
          {t("label.cost_center")}
        </span>
        <p className="text-[15px] text-white">{name}</p>
      </div>
    </Card>
  );
};

const renderStatus = (status: string | undefined, t: TFunction) => {
  if (!status) return null;

  const invoiceStatus = INVOICE_STATUS.find((sts) => sts.value === status);

  return (
    <Card className="h-[66px] py-[14px]">
      <div className="flex flex-col items-start justify-between">
        <span className="text-xs leading-4 text-white/50">
          {t("label.status")}
        </span>
        <p className="flex items-center gap-1 text-[15px] text-white">
          <span>{t(`invoice_status.${invoiceStatus?.translationKey}`)}</span>
        </p>
      </div>
    </Card>
  );
};

const renderIssueDate = (date: Date | string | undefined, t: TFunction) => {
  if (!date) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">{t("label.date")}</span>
      <span className="text-[15px] text-white">
        {formatDate(new Date(date), "dd MMMM yyyy - HH:mm")}
      </span>
    </Card>
  );
};

const renderDueDate = (date: Date | string | undefined, t: TFunction) => {
  if (!date) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.due_date")}
      </span>
      <span className="text-[15px] text-white">
        {formatDate(new Date(date), "dd MMMM yyyy - HH:mm")}
      </span>
    </Card>
  );
};

const FilesPreview = ({ files }: { files: Attachment[] }) => {
  const { t } = useTranslation();
  const [urls, setUrls] = useState<string[]>([]);

  useEffect(() => {
    const getPreviewUrls = async () => {
      if (!files || files.length === 0) {
        setUrls([]);
        return;
      }

      try {
        const urlResults = await Promise.all(
          files.map(async (file) => {
            const res = await api.get<ApiResponse<string>>(
              `${BASE_URL}/invoices/attachments/${file.id}/url`
            );
            return res.data;
          })
        );
        setUrls(urlResults.map((result) => result.value));
      } catch (error) {
        console.error("Error fetching attachment URLs:", error);
        setUrls([]);
      }
    };

    getPreviewUrls();
  }, [files]);

  if (!files || files.length === 0) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.files")}
      </span>
      <FilePreview className="mt-1" urls={urls} />
    </Card>
  );
};

const renderNote = (note: string | undefined, t: TFunction) => {
  if (!note || note === "") return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">{t("label.note")}</span>
      <p className="text-[15px] text-white">{note}</p>
    </Card>
  );
};

const renderVendor = (vendor: Vendor | undefined, t: TFunction) => {
  if (!vendor) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.vendor")}
      </span>
      <p className="text-[15px] text-white">{vendor.name}</p>
    </Card>
  );
};

const renderInvoiceNumber = (
  invoiceNumber: string | undefined,
  t: TFunction
) => {
  if (!invoiceNumber) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.invoice_number")}
      </span>
      <p className="text-[15px] text-white">{invoiceNumber}</p>
    </Card>
  );
};

const renderBudget = (budget: Budget | undefined, t: TFunction) => {
  if (!budget) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.budget")}
      </span>
      <p className="text-[15px] text-white">{budget.name}</p>
    </Card>
  );
};

const renderVessel = (vessel: Vessel | undefined, t: TFunction) => {
  if (!vessel) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="text-xs leading-4 text-white/50">
        {t("label.vessel")}
      </span>
      <p className="text-[15px] text-white">{vessel.name}</p>
    </Card>
  );
};

const renderLineItems = (
  lineItems: InvoiceLineItem[] | undefined,
  t: TFunction,
  currency: Currency | undefined
) => {
  if (!lineItems || lineItems.length === 0) return null;

  return (
    <Card className="flex flex-col items-start justify-center gap-[2px] py-[14px]">
      <span className="mb-2 text-xs leading-4 text-white/50">
        {t("label.items")}
      </span>

      <div className="flex w-full flex-col gap-2">
        {lineItems.map((lineItem) => (
          <div
            key={lineItem.id}
            data-testid={`line-item-${lineItem.id}`}
            className="w-full border-b border-white/10 py-3 first:pt-0 last:border-b-0 last:pb-0"
          >
            <p className="mb-2 text-[15px] font-medium text-white">
              {lineItem.description}
            </p>
            <div className="flex flex-wrap gap-10 gap-y-1 text-sm sm:gap-x-20">
              <div className="flex flex-col gap-1">
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.amount")}:
                  </span>
                  {currencyFormatter(lineItem.amount, currency?.code)}
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.quantity")}:
                  </span>
                  {lineItem.quantity}
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.unit_price")}:
                  </span>
                  {currencyFormatter(lineItem.unitPrice, currency?.code)}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.vat_rate")}:
                  </span>
                  {lineItem.vatRate}%
                </p>
                <p className="text-white/70">
                  <span className="mr-1 text-white/50">
                    {t("label.vat_amount")}:
                  </span>
                  {currencyFormatter(lineItem.vatAmount, currency?.code)}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default InvoiceDetail;
